import { Component, h, Host, Prop, State } from '@stencil/core'
import Axios, { AxiosResponse } from 'axios'
import { FathomLogo } from 'src/customize/integrations/logos'

import { ICallAskFathom } from '../types'
import { IAskFathomMessage, IAskMessagesResponse } from './types'

const getMessagesWithContent = (messages: IAskFathomMessage[]) => messages.filter((message) => !!message.content)

@Component({
  tag: 'page-call-detail-ask-fathom',
})
export class PageCallDetailAskFathom {
  @Prop() askFathom: ICallAskFathom

  @State() messages: IAskFathomMessage[] = []

  scrollEl: HTMLUiScrollareaElement

  handleSubmit = async ({ detail: message }) => {
    try {
      this.messages = [
        ...this.messages,
        { role: 'user', content: message },
        { role: 'assistant', isLoadingContent: true },
      ]

      const messagesResponse = await Axios.post<any, AxiosResponse<IAskMessagesResponse>>(
        this.askFathom.actions.messagesUrl,
        { messages: getMessagesWithContent(this.messages) }
      )

      this.messages = this.messages.map((existingMessage, index) => {
        if (index === this.messages.length - 1) {
          return {
            ...messagesResponse.data.next_message,
            isLoadingContent: false,
            isLoadingClips: messagesResponse.data.include_clips,
          }
        }
        return existingMessage
      })

      if (messagesResponse.data.include_clips) {
        this.getClips(messagesResponse.data.next_message.id)
      }
    } catch (error) {
      this.messages = this.messages.map((existingMessage, index) => {
        if (index === this.messages.length - 1) {
          return {
            ...existingMessage,
            isLoadingContent: false,
            hasLoadingContentError: true,
          }
        }
        return existingMessage
      })
    }
  }

  getClips = async (messageId: string) => {
    try {
      const clipsResponse = await Axios.post(this.askFathom.actions.clipsUrl, {
        messages: getMessagesWithContent(this.messages),
      })

      this.messages = this.messages.map((existingMessage) => {
        if (existingMessage.id === clipsResponse.data.message_id) {
          return { ...existingMessage, isLoadingClips: false, clips: clipsResponse.data.clips }
        }
        return existingMessage
      })
    } catch (error) {
      this.messages = this.messages.map((existingMessage) => {
        if (existingMessage.id === messageId) {
          return { ...existingMessage, isLoadingClips: false }
        }
        return existingMessage
      })
    }
  }

  render() {
    return (
      <Host class='flex overflow-y-hidden relative flex-col flex-grow px-4 bg-black'>
        <ui-scrollarea
          class='flex-1'
          shadowColor='transparent'
          ref={(el) => (this.scrollEl = el)}
          shadowStyleY={{ display: 'none' }}
          overflowX={false}
        >
          {this.messages.length === 0 && (
            <div class='flex flex-col justify-center items-center h-100%'>
              <div class='p-3 mb-2 w-14 h-14 bg-gray-800 rounded-full border-gray-950' style={{ borderWidth: '10px' }}>
                <FathomLogo class='w-10' />
              </div>
              <p class='mb-10 text-xlarge font-bold'>Hi, what can I tell you about this meeting?</p>
              <ul class='grid grid-cols-2 gap-2 mb-4 text-sm text-gray-30'>
                {this.askFathom?.suggested_messages?.map((suggestedMessage) => (
                  <li
                    key={suggestedMessage}
                    class='p-2 bg-gray-850 hover:bg-opacity-80 rounded-xl border border-gray-600 cursor-pointer'
                    onClick={() => {
                      this.handleSubmit({ detail: suggestedMessage })
                    }}
                  >
                    {suggestedMessage}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {this.messages.map((message, index) => (
            <page-call-detail-ask-message
              message={message}
              key={`${index}.${message.role}`}
              onMessageLoaded={({ detail: messageEl }) => {
                this.scrollEl.scrollToElement(messageEl)
              }}
            />
          ))}
        </ui-scrollarea>

        <page-call-detail-ask-fathom-form
          class='py-4'
          isNextMessageLoading={this.messages[this.messages.length - 1]?.isLoadingContent}
          onSubmittedMessage={this.handleSubmit}
        />
      </Host>
    )
  }
}
